import { Collection } from "@/models/collection";
import Proposal from "@/models/proposal";
import { ProposalService } from "@/services/proposal.service";
import { tap } from "rxjs/operators";
import CommentHeader from "@/models/comment-header";
import { Paginate } from "@/models/paginate";
import { Observable } from "rxjs-compat";
import { ActionContext, Module } from "vuex";
import { StoreData } from "@/store";
import UpdateProposalCommentDto from "@/dto/update-proposal-comment.dto";
import ProposalDetail from "@/models/proposal-detail";

export interface ProposalData {
  proposals: Paginate<Array<Collection<Proposal>>> | null;
  subordinateProposals: Paginate<Array<Collection<Proposal>>> | null;
  proposal: Collection<Proposal> | null;
  isAllowToSubmit: {
    isAllowToSubmit: boolean;
    message: string;
  } | null;
  mainComments: CommentHeader[];
  comments: CommentHeader[];
  commiteeComments: CommentHeader[];
  previousProposals: any | null;
}

export default {
  namespaced: true,
  state: (): ProposalData => ({
    proposals: null,
    subordinateProposals: null,
    proposal: null,
    isAllowToSubmit: null,
    mainComments: [],
    comments: [],
    commiteeComments: [],
    previousProposals: [],
  }),
  actions: {
    getProposals: (
      context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      // context.state.proposal = null;
      return ProposalService.getProposals(params).pipe(
        tap((proposals: Paginate<Array<Collection<Proposal>>>): any => {
          context.commit("setProposals", proposals);
        }),
      );
    },

    getProposal: (
      context: ActionContext<ProposalData, StoreData>,
      proposalId: string,
    ): any => {
      // context.state.proposal = null;
      return ProposalService.getProposal(proposalId).pipe(
        tap((proposal: Collection<Proposal>): void => {
          context.commit("setProposal", proposal);
        }),
      );
    },

    postProposal: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.postProposal(params);
    },

    updateProposal: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.updateProposal(params);
    },

    updateStatusProposal: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.updateStatusProposal(params);
    },

    updateStatusProposalBatch: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.updateStatusProposalBatch(params);
    },

    deleteProposal: (
      _context: ActionContext<ProposalData, StoreData>,
      proposalId: string,
    ): any => {
      return ProposalService.deleteProposal(proposalId);
    },

    updateOrder: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.updateOrder(params);
    },

    setProposalApproval: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.setProposalApproval(params);
    },

    addJudge: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): Observable<Collection<Proposal>> => {
      return ProposalService.addJudge(params);
    },

    deleteJudge: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): Observable<Collection<Proposal>> => {
      return ProposalService.deleteJudge(params);
    },

    finalizeJuryDetail: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.finalizeJuryDetail(params);
    },

    getJudgeProposals: (
      context: ActionContext<ProposalData, StoreData>,
      { params, options }: { params: any; options: any },
    ): any => {
      return ProposalService.getJudgeProposals(params, options).pipe(
        tap((proposals: Array<Collection<Proposal>>): any => {
          context.commit("setProposals", proposals);
        }),
      );
    },

    checkAllowToSubmit: (
      context: ActionContext<ProposalData, StoreData>,
    ): any => {
      return ProposalService.isAllowToSubmit().pipe(
        tap((isAllowToSubmit): void => {
          context.commit("setAllowToSubmit", isAllowToSubmit);
        }),
      );
    },

    getParticipantProposals: (
      context: ActionContext<ProposalData, StoreData>,
    ): any => {
      return ProposalService.getParticipantProposals().pipe(
        tap((proposals: Array<Collection<Proposal>>): void => {
          context.commit("setProposals", proposals);
        }),
      );
    },

    getSubordinateProposals: (
      context: ActionContext<ProposalData, StoreData>,
    ): any => {
      return ProposalService.getSubordinateProposals().pipe(
        tap((proposals: Array<Collection<Proposal>>): void => {
          context.commit("setSubordinateProposals", proposals);
        }),
      );
    },

    saveProposalSummary: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.saveProposalSummary(params);
    },

    saveProposalBody: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.saveProposalBody(params);
    },

    createProposalLiterature: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.createProposalLiterature(params);
    },

    updateProposalLiterature: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.updateProposalLiterature(params);
    },

    deleteProposalLiterature: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.deleteProposalLiterature(params);
    },

    saveProposalImplementation: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.saveProposalImplementation(params);
    },

    createProposalReference: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.createProposalReference(params);
    },

    updateProposalReference: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.updateProposalReference(params);
    },

    deleteProposalReference: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.deleteProposalReference(params);
    },

    createProposalAppendix: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.createProposalAppendix(params);
    },

    updateProposalAppendix: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.updateProposalAppendix(params);
    },

    deleteProposalAppendix: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.deleteProposalAppendix(params);
    },

    finalizeProposal: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.finalizeProposal(params);
    },

    savePoster: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.savePoster(params);
    },

    getMainComments: (
      context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      // context.state.mainComments = [];
      return ProposalService.getProposalComments(params).pipe(
        tap((comments): void => {
          context.commit("setMainComments", comments);
        }),
      );
    },

    getComments: (
      context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      // context.state.comments = [];
      return ProposalService.getProposalComments(params).pipe(
        tap((comments): void => {
          context.commit("setComments", comments);
        }),
      );
    },

    getCommiteeComments: (
      context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      // context.state.comments = [];
      return ProposalService.getProposalCommiteeComments(params).pipe(
        tap((comments): void => {
          context.commit("setCommiteeComments", comments);
        }),
      );
    },

    postComment: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.postProposalComment(params);
    },

    postCommentCommitee: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ) => {
      return ProposalService.postCommiteeProposalComment(params);
    },

    updateComment: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.putProposalComment(params);
    },

    updateCommiteeComment: (
      _context: ActionContext<ProposalData, StoreData>,
      params: UpdateProposalCommentDto,
    ): any => {
      return ProposalService.patchProposalComment(params);
    },

    deleteComment: (
      _context: ActionContext<ProposalData, StoreData>,
      commentId: string,
    ): any => {
      return ProposalService.deleteProposalComment(commentId);
    },

    saveTop15: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.saveTop15(params);
    },

    saveTop3: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.saveTop3(params);
    },

    sendEmailReminderToFinalize: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.sendEmailReminderToFinalize(params);
    },

    sendEmailReminderToApproval: (
      _context: ActionContext<ProposalData, StoreData>,
      params: any,
    ): any => {
      return ProposalService.sendEmailReminderToApproval(params);
    },

    getBudgetProposal: (
      _context: ActionContext<ProposalData, StoreData>,
      proposalId: string,
    ): any => {
      return ProposalService.getBudgetProposal(proposalId);
    },

    updateProposalDetail: (
      _context: ActionContext<ProposalData, StoreData>,
      proposalDetail: ProposalDetail,
    ): any => {
      return ProposalService.updateProposalDetail(proposalDetail);
    },

    updateProposalSummary: (
      _context: ActionContext<ProposalData, StoreData>,
      proposal: Proposal,
    ): any => {
      return ProposalService.updateProposalSummary(proposal);
    },

    updateProposalKeyword: (
      _context: ActionContext<ProposalData, StoreData>,
      proposal: Proposal,
    ): any => {
      return ProposalService.updateProposalKeyword(proposal);
    },

    getPreviousProposal:(
      context: ActionContext<ProposalData, StoreData>,
    ): any => {
      return ProposalService.getPreviousProposal().pipe(
        tap((previousProposals): void => {
          context.commit("setPreviousProposals", previousProposals);
        }),
      );
    },
  },
  mutations: {
    setProposals: (
      state: ProposalData,
      proposals: Paginate<Array<Collection<Proposal>>>,
    ): void => {
      state.proposals = proposals;
    },

    setSubordinateProposals: (
      state: ProposalData,
      proposals: Paginate<Array<Collection<Proposal>>>,
    ): void => {
      state.subordinateProposals = proposals;
    },

    setProposal: (
      state: ProposalData,
      proposal: Collection<Proposal>,
    ): void => {
      state.proposal = proposal;
    },

    setAllowToSubmit: (state: ProposalData, isAllowToSubmit: any): void => {
      state.isAllowToSubmit = isAllowToSubmit;
    },

    setMainComments: (state: ProposalData, comments: CommentHeader[]): void => {
      state.mainComments = comments;
    },

    setComments: (state: ProposalData, comments: CommentHeader[]): void => {
      state.comments = comments;
    },

    setCommiteeComments: (
      state: ProposalData,
      comments: CommentHeader[],
    ): void => {
      state.commiteeComments = comments;
    },

    setPreviousProposals: (
      state: ProposalData,
      previousProposals: any,
    ): void => {
      state.previousProposals = previousProposals;
    }
  },
} as Module<ProposalData, StoreData>;
